import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";
import { useNavigate } from "react-router-dom";

import Header from "../components/Header";
import NotificationModal from "../components/NotificationModal";
import "./CadastroBabysitter.css"; // Reutiliza o CSS do fluxo conversacional
import { Check, X, Clock, Loader, Camera, ChevronLeft } from "lucide-react";
import { uploadToStorage, compressImageToWebP } from "../utils/uploadToStorage";
import ImageCropperModal from "../components/ImageCropperModal";

const MAX_DOC_SIZE_BYTES = 5 * 1024 * 1024;

const isAllowedCriminalType = (file) => {
  if (!file?.type) return false;
  return file.type === "application/pdf" || file.type.startsWith("image/");
};

/* =========================================================
   HELPER — CLONE SEGURO DO FILE (ESSENCIAL)
========================================================= */

/* =========================================================
   COMPONENTE PRINCIPAL
========================================================= */
export default function VerificadorIdentidade({ hideHeader = false, onClose }) {
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState("incompleto");
  const [step, setStep] = useState(0);

  const [userProfile, setUserProfile] = useState({ type: null, table: null });

  const [files, setFiles] = useState({
    frente: null,
    verso: null,
    selfie: null,
    criminal: null,
  });

  const [previewUrls, setPreviewUrls] = useState({
    frente: null,
    verso: null,
    selfie: null,
    criminal: null,
  });
  const [criminalFileName, setCriminalFileName] = useState("");

  // Estados para o Cropper
  const [cropperOpen, setCropperOpen] = useState(false);
  const [tempImgSrc, setTempImgSrc] = useState(null);
  const [activeField, setActiveField] = useState(null); // 'frente', 'verso', 'selfie'

  const [notification, setNotification] = useState({
    message: "",
    type: "",
    show: false,
  });

  const steps = [
    {
      key: "frente",
      label: "Frente do Documento",
      pergunta:
        "Estamos acabando! Essa é a ultima parte.\n\nPara sua segurança, precisamos validar sua identidade.\n\nEnvie a FRENTE do seu documento (RG ou CNH).",
    },
    {
      key: "verso",
      label: "Verso do Documento",
      pergunta: "Ótimo! Agora envie o VERSO do mesmo documento.",
    },
    {
      key: "selfie",
      label: "Selfie com o Documento",
      pergunta:
        "Para finalizar a verificação, envie uma SELFIE segurando seu documento ao lado do rosto.",
    },
    {
      key: "criminal",
      label: "Antecedentes Criminais",
      pergunta:
        "Para maior segurança, precisamos da sua Certidão de Antecedentes Criminais da Polícia Federal.\n\nSe ainda não tem, emita aqui no site da PF:",
    },
    {
      key: "final",
      label: "Finalizar",
      pergunta: "Tudo pronto! Vamos enviar seus documentos para análise?",
    },
  ];

  const closeNotification = () =>
    setNotification({ message: "", type: "", show: false });

  /* =========================================================
     INICIALIZA PERFIL
  ========================================================= */
  const initializeProfile = useCallback(async (userId) => {
    try {
      const { data: usuario, error: userError } = await supabase.rpc(
        "rpc_get_user_auth_info",
        {
          p_user_id: userId,
        },
      );

      if (userError) {
        console.error("Erro ao buscar info de autenticação:", userError);
        throw userError;
      }

      const type = usuario?.tipo;
      if (!type) {
        setNotification({
          message: "Tipo de perfil não reconhecido ou incompleto.",
          type: "error",
          show: true,
        });
        return;
      }

      // A tabela agora é sempre 'usuarios'
      setUserProfile({ type, table: "usuarios" });
      setStatus(usuario?.doc_status || "incompleto");
    } catch (error) {
      console.error(error);
      setNotification({
        message: "Erro ao carregar seu perfil.",
        type: "error",
        show: true,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        navigate("/login");
        return;
      }
      setSession(session);
      initializeProfile(session.user.id);
    });
  }, [navigate, initializeProfile]);

  /* =========================================================
     FILE CHANGE
  ========================================================= */
  const handleFileChange = async (e, type) => {
    let file = e.target.files[0];
    if (!file) return;

    if (file.size > MAX_DOC_SIZE_BYTES) {
      setNotification({
        message: "Arquivo maior que 5MB. Envie um arquivo menor.",
        type: "error",
        show: true,
      });
      e.target.value = null;
      return;
    }

    if (type === "criminal") {
      if (!isAllowedCriminalType(file)) {
        setNotification({
          message: "Antecedentes: use PDF ou imagem.",
          type: "error",
          show: true,
        });
        e.target.value = null;
        return;
      }

      if (previewUrls.criminal) {
        URL.revokeObjectURL(previewUrls.criminal);
      }

      const criminalPreview = file.type.startsWith("image/")
        ? URL.createObjectURL(file)
        : null;

      setFiles((prev) => ({ ...prev, criminal: file }));
      setPreviewUrls((prev) => ({ ...prev, criminal: criminalPreview }));
      setCriminalFileName(file.name || "documento-criminal");
      e.target.value = null;
      return;
    }

    try {
      // Pré-processa para garantir que HEIC ou imagens grandes sejam lidas
      const processedFile = await compressImageToWebP(file, {
        maxDimension: 2000,
        maxSizeBytes: 5 * 1024 * 1024, // Limite alto apenas para leitura
      });
      const url = URL.createObjectURL(processedFile);
      setTempImgSrc(url);
      setActiveField(type);
      setCropperOpen(true);
      e.target.value = null; // Reset input
    } catch (err) {
      console.error("Erro ao processar imagem:", err);
      setNotification({
        message: "Não foi possível processar o arquivo. Tente outra imagem.",
        type: "error",
        show: true,
      });
      e.target.value = null;
    }
  };

  const handleCropComplete = (croppedBlob) => {
    if (!activeField) return;

    if (previewUrls[activeField]) {
      URL.revokeObjectURL(previewUrls[activeField]);
    }

    setFiles((prev) => ({ ...prev, [activeField]: croppedBlob }));
    setPreviewUrls((prev) => ({
      ...prev,
      [activeField]: URL.createObjectURL(croppedBlob),
    }));
    setCropperOpen(false);
    setTempImgSrc(null);
    setActiveField(null);
  };

  const handleNext = () => {
    const currentKey = steps[step].key;
    if (currentKey !== "final" && !files[currentKey]) {
      setNotification({
        message: "Por favor, selecione o arquivo solicitado.",
        type: "error",
        show: true,
      });
      return;
    }
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };

  /* =========================================================
     UPLOAD DE ARQUIVO (SEGURO)
  ========================================================= */

  /* =========================================================
     SUBMIT
  ========================================================= */
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (isSubmitting || status === "enviado") return;

    if (!files.frente || !files.verso || !files.selfie || !files.criminal) {
      setNotification({
        message: "Envie todos os documentos.",
        type: "error",
        show: true,
      });
      return;
    }

    setIsSubmitting(true);
    setStatus("enviando");

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error("Sessão expirada. Faça login novamente.");
      const userId = session.user.id;

      const isBaba =
        userProfile.type === "baba" || userProfile.type === "babysitter";
      const frenteKey = isBaba ? "certificados" : "documento-familia";
      const versoKey = isBaba ? "certificados" : "documento-familia";
      const selfieKey = isBaba ? "certificados" : "documento-familia";
      const criminalKey = "certificados"; // Bucket 'documentos', subpasta 'certificados' (confirmado funcional em CadastroBabysitter)

      // 1. Upload dos arquivos diretamente para o Storage (Supabase)
      const [frenteRes, versoRes, selfieRes, criminalRes] = await Promise.all([
        uploadToStorage(files.frente, frenteKey),
        uploadToStorage(files.verso, versoKey),
        uploadToStorage(files.selfie, selfieKey),
        uploadToStorage(files.criminal, criminalKey),
      ]);

      const docData = {
        frente: frenteRes.path,
        verso: versoRes.path,
        selfie: selfieRes.path,
        criminal: criminalRes.path,
      };

      const { error: rpcError } = await supabase
        .from("usuarios")
        .update({ documentos_url: docData, doc_status: "enviado" })
        .eq("id", userId);

      if (rpcError) throw rpcError;

      const redirectPath =
        userProfile.type === "baba" || userProfile.type === "babysitter"
          ? "/dashboard-baba"
          : "/dashboard-familia";

      setNotification({
        message: "Documentos enviados com sucesso!",
        type: "success",
        show: true,
      });

      // Atraso intencional para o usuário ler a notificação de sucesso
      setTimeout(() => {
        setStatus("enviado");
        navigate(redirectPath);
      }, 2000);
    } catch (error) {
      console.error("Erro no upload:", error);
      setNotification({
        message: `Erro ao enviar documentos: ${
          error.message || "Tente novamente."
        }`,
        type: "error",
        show: true,
      });
      setStatus("incompleto");
    } finally {
      setIsSubmitting(false);
    }
  };

  /* =========================================================
     UI
  ========================================================= */
  const currentStep = steps[step];
  const isDisabled =
    loading || isSubmitting || status === "enviado" || !session;

  if (!loading && status === "enviado") {
    return (
      <>
        {!hideHeader && <Header showNavLinks={false} />}
        <div className="cadastro-chat">
          <div className="chat-box">
            <p className="pergunta">⏳ Documentos Enviados!</p>
            <div className="resposta">
              <p>
                Seus documentos estão em análise. Você será notificado assim que
                o processo for concluído.
              </p>
              <button
                className="btn-primary mt-4"
                onClick={() => {
                  if (hideHeader && onClose) {
                    onClose();
                  } else {
                    const path =
                      userProfile.type === "babysitter" ||
                      userProfile.type === "baba"
                        ? "/dashboard-baba"
                        : "/dashboard-familia";
                    navigate(path);
                  }
                }}
              >
                {hideHeader ? "Concluir" : "Ir para o Dashboard"}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {!hideHeader && <Header showNavLinks={false} />}

      <NotificationModal
        show={notification.show}
        message={notification.message}
        type={notification.type}
        onClose={closeNotification}
      />

      <div className="cadastro-chat">
        <div className="chat-box">
          <div className="chat-progress">
            <div
              className="chat-progress-bar"
              style={{ width: `${((step + 1) / steps.length) * 100}%` }}
            />
          </div>

          <p className="pergunta">{currentStep.pergunta}</p>

          <div className="resposta">
            {currentStep.key === "criminal" && (
              <a
                href="https://servicos.pf.gov.br/epol-sinic-publico/"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "block",
                  marginBottom: "15px",
                  color: "var(--color-primary)",
                  fontWeight: "bold",
                  textDecoration: "underline",
                }}
              >
                🌐 Emitir Certidão no site da Polícia Federal
              </a>
            )}
            {currentStep.key !== "final" ? (
              <div className="file-input-wrapper">
                <label
                  htmlFor={`file-${currentStep.key}`}
                  className="file-label"
                >
                  {previewUrls[currentStep.key] ? (
                    <img
                      src={previewUrls[currentStep.key]}
                      alt="Preview"
                      className="foto-preview-chat"
                    />
                  ) : currentStep.key === "criminal" && files.criminal ? (
                    <div className="file-placeholder">
                      <Check size={32} />
                      <span>{criminalFileName || "Documento anexado"}</span>
                    </div>
                  ) : (
                    <div className="file-placeholder">
                      <Camera size={40} />
                      <span>Tirar ou escolher foto</span>
                    </div>
                  )}
                </label>
                <input
                  id={`file-${currentStep.key}`}
                  type="file"
                  accept={
                    currentStep.key === "criminal"
                      ? "image/*,application/pdf"
                      : "image/*,.heic,.heif"
                  }
                  disabled={isDisabled}
                  onChange={(e) => handleFileChange(e, currentStep.key)}
                  hidden
                />
              </div>
            ) : (
              <div className="final-step">
                <p>
                  Ao clicar em enviar, seus documentos serão analisados pela
                  nossa equipe. Você receberá uma notificação assim que seu
                  perfil for aprovado!
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="chat-actions">
          {step > 0 && (
            <button
              className="btn-back btn-secondary"
              onClick={() => setStep(step - 1)}
              disabled={isSubmitting}
            >
              <ChevronLeft size={18} /> Voltar
            </button>
          )}
          <button
            className="btn-next btn-primary"
            onClick={handleNext}
            disabled={isDisabled}
          >
            {isSubmitting ? (
              <>
                <Loader className="loader-icon" /> Enviando...
              </>
            ) : step === 4 ? (
              "Enviar para verificação"
            ) : (
              "Continuar"
            )}
          </button>
        </div>
      </div>

      {cropperOpen && tempImgSrc && (
        <ImageCropperModal
          imageSrc={tempImgSrc}
          // Se for selfie, usa quadrado (1). Para documentos, usa proporção de cartão de crédito (~1.586)
          aspect={activeField === "selfie" ? 1 : 1.586}
          onCropComplete={handleCropComplete}
          onClose={() => setCropperOpen(false)}
        />
      )}
    </>
  );
}
